import React from "react"
import { Button, Modal, Table, Spinner, Col, Form, Row } from "react-bootstrap"
import { toast } from "react-toastify"
import { Subscription, forkJoin } from "rxjs"
import ActionButton from "../ActionButton"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import { RouteComponentProps } from "react-router"
import { Home } from "../../config/Routes"
import Checkbox from "../Checkbox"
import { APIError } from "../../api/ObservableFromFetch"
import { Typeahead } from "react-bootstrap-typeahead"
import { FaAngleDown } from "react-icons/fa"
import AutoFilterCompany from "../../api/Companies/CompanyModel/AutoFilterCompany"
import FormatError from "../../formatters/FormatError"
import OrderDefaultsGet, { OrderDefault, OrderDefaultsReset, OrderDefaultsSave, SaveOrderDefaults } from "../../api/Administration/OrderDefaults"
import LabServiceRegulatoryModesGetObservable from "../../api/LabService/LabServiceRegulatoryModesGet"
import RegulatoryMode from "../../api/LabService/LabServiceModel/RegulatoryMode"
import EmployeeCategoriesGetObservable from "../../api/LabService/EmployeeCategoriesGet"
import EmployeeCategory from "../../api/LabService/LabServiceModel/EmployeeCategory"
import { TestReasonsObservable } from "../../api/OrderRequests/OrderRequestsConstraintGet"
import { TestReasonType } from "../../api/OrderRequests/OrderRequestsModel/TestReasonType"
import NumericInput from "react-numeric-input"
import { AppState } from "../../AppState"
import { connect } from "react-redux"
import { isSiteAdmin, isPortalAdmin } from "../../authentication/isAdminRole"
import ImplementationsGetObservable from "../../api/Implementations/ImplementationsGet"
import ImplementationData from "../../api/Implementations/Model/ImplementationData"
import CompanyInput from "../FormattedInputs/CompanyInput"

export enum DefaultLookupCode {
  RegMode = "REGMODE",
  TestReason = "REASON",
  ExpTime = "EXPIRATION",
  FAAEmployee = "FAAEMPLOYEE"
}

interface Lookup {
  Code: DefaultLookupCode
  Label: string
  IsInput: boolean
  IsNumeric: boolean
}

const LookupMap: Lookup[] = [
  {Code: DefaultLookupCode.RegMode, Label: "Regulatory Mode", IsInput: false, IsNumeric: false},
  {Code: DefaultLookupCode.TestReason, Label: "Reason", IsInput: false, IsNumeric: false},
  {Code: DefaultLookupCode.ExpTime, Label: "Hours Until Order Expires", IsInput: true, IsNumeric: true},
  {Code: DefaultLookupCode.FAAEmployee, Label: "FAA Employee Category", IsInput: false, IsNumeric: false}
]

interface OrderDefaultsAuthProps {
  isPortalAdmin?: boolean
  isSiteAdmin?: boolean
}

interface OrderDefaultsProps { }

interface OrderDefaultsState {
  saving: boolean
  loading: boolean
  resetting: boolean
  loadingDefaults: boolean
  portalDefaults: OrderDefault[]
  defaults: OrderDefault[]
  data: OrderDefault[]
  portals: ImplementationData[]
  selectedPortal?: ImplementationData
  selectedCompany?: AutoFilterCompany
  selectedLookup?: Lookup
  showWarning: boolean
}

export class OrderDefaults extends React.PureComponent<OrderDefaultsProps & OrderDefaultsAuthProps, OrderDefaultsState> {
  emptyState: OrderDefaultsState = {
    loading: false,
    saving: false,
    resetting: false,
    loadingDefaults: false,
    portalDefaults: [],
    defaults: [],
    data: [],
    portals: [],
    selectedPortal: undefined,
    selectedCompany: undefined,
    selectedLookup: undefined,
    showWarning: false
  }
  state: OrderDefaultsState = { ...this.emptyState }

  portalTypeahead = React.createRef<Typeahead<ImplementationData>>()
  loadPortalsSubscription?: Subscription
  loadCompanyDefaultsSubscription?: Subscription
  loadPortalDefaultsSubscription?: Subscription
  saveSubscription?: Subscription

  componentDidMount() {
    this.setState({loading: true})

    if (this.props.isSiteAdmin) {
      this.loadPortalsSubscription = ImplementationsGetObservable().subscribe(
        data => this.setState({ portals: data.Records, loading: false }),
        error =>  this.handleAPIError(error, { loading: false, portals: [] })
      )
    }


    if (this.props.isPortalAdmin) {
      this.loadPortalDefaultsSubscription = OrderDefaultsGet({ImplementationID: 0, AccountID: 0, LookupCode: ""})
      .subscribe(
        data => this.setState({portalDefaults: data, loading: false}),
        error => this.handleAPIError(error, {})
      )
    }
  }

  componentWillUnmount() {
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.loadPortalsSubscription && this.loadPortalsSubscription.unsubscribe()
    this.loadPortalDefaultsSubscription && this.loadPortalDefaultsSubscription.unsubscribe()
    this.loadCompanyDefaultsSubscription && this.loadCompanyDefaultsSubscription.unsubscribe()
  }

  loadDefaults = (lookup?: Lookup) => {
    this.setState({loadingDefaults: true})
    let defaults: OrderDefault[] = []
    const requestData = { ImplementationID: this.state.selectedPortal?.ImplementationID || 0, AccountID: this.state.selectedCompany?.AccountID, LookupCode: lookup?.Code || "" }
    switch (lookup?.Code) {
      case DefaultLookupCode.RegMode:
        this.loadCompanyDefaultsSubscription = forkJoin({
          modes: LabServiceRegulatoryModesGetObservable(true),
          modeDefaults: OrderDefaultsGet(requestData)
        }).subscribe(
          ({ modes, modeDefaults }) => {
            defaults = this.remapToOrderDefault(modes, modeDefaults, lookup.Code)
            this.setState({loadingDefaults: false, defaults: defaults, data: defaults, showWarning: modeDefaults.length === 0})
          },
          (error) => this.handleAPIError(error, { loadingDefaults: false, defaults: [] })
        )
        break
      case DefaultLookupCode.TestReason:
        this.loadCompanyDefaultsSubscription = forkJoin({
          reasons: TestReasonsObservable(),
          reasonDefaults: OrderDefaultsGet(requestData)
        }).subscribe(
          ({ reasons, reasonDefaults }) => {
            defaults = this.remapToOrderDefault(reasons, reasonDefaults, lookup.Code)
            this.setState({loadingDefaults: false, defaults: defaults, data: defaults, showWarning: reasonDefaults.length === 0})
          },
          (error) => this.handleAPIError(error, { loadingDefaults: false, defaults: [] })
        )
        break
      case DefaultLookupCode.ExpTime:
          this.loadCompanyDefaultsSubscription = OrderDefaultsGet(requestData)
          .subscribe(
            (timeDefaults) => {
              defaults = this.singleDefault(lookup.Label, timeDefaults, lookup.Code)
              this.setState({loadingDefaults: false, defaults: defaults, data: defaults, showWarning: timeDefaults.length === 0})
            },
            (error) => this.handleAPIError(error, { loadingDefaults: false, defaults: [] })
          )
          break
      case DefaultLookupCode.FAAEmployee:
          this.loadCompanyDefaultsSubscription = forkJoin({
            categories: EmployeeCategoriesGetObservable(3), //3 to get FAA employee categories
            categoryDefaults: OrderDefaultsGet(requestData)
          }).subscribe(
            ({ categories, categoryDefaults }) => {
              defaults = this.remapToOrderDefault(categories, categoryDefaults, lookup.Code)
              this.setState({loadingDefaults: false, defaults: defaults, data: defaults, showWarning: categoryDefaults.length === 0})
            },
            (error) => this.handleAPIError(error, { loadingDefaults: false, defaults: [] })
          )
          break
      default:
        this.setState({loadingDefaults: false, defaults: [], data: []})
        break
    }
  }

  createOrderDefaultElement = (fieldName: string, lookup: string, existingDefault?: OrderDefault): OrderDefault => {
    return {
      OrderDefaultID: existingDefault?.OrderDefaultID,
      ImplementationID:  existingDefault?.ImplementationID || this.state.selectedPortal?.ImplementationID || 0,
      AccountID: existingDefault?.AccountID || this.state.selectedCompany?.AccountID,
      LookupCode: existingDefault?.LookupCode || lookup,
      FieldName: fieldName,
      DefaultValue: existingDefault?.DefaultValue,
      Show: existingDefault?.Show || false,
      IsDefault: existingDefault?.IsDefault || false,
      Disable: !this.elementVisibleOnPortalLevel(existingDefault?.LookupCode || lookup, fieldName)
    }
  }

  elementVisibleOnPortalLevel = (lookup: string, fieldName: string) => {
    if (this.state.portalDefaults.length === 0) {
      return true
    }
    const relatedDefault = this.state.portalDefaults.find(el => el.LookupCode === lookup && el.FieldName === fieldName)
    if (relatedDefault) {
      return relatedDefault.Show
    }
    else {
      return true
    }
  }

  singleDefault = (fieldName: string, defaults: OrderDefault[], lookup: string) => {
    const savedDefault = defaults.find(x => x.FieldName === fieldName)
    const newEl = this.createOrderDefaultElement(fieldName, lookup, savedDefault)
    return [newEl]
  }

  remapToOrderDefault = (objects: RegulatoryMode[] | TestReasonType[] | EmployeeCategory[], defaults: OrderDefault[], lookup: string) : OrderDefault[]  => {
    let result: OrderDefault[] = []
    objects.forEach((obj: RegulatoryMode | TestReasonType | EmployeeCategory) => {
      let FieldName = ""

      if ("Name" in obj) {
        FieldName = obj.Name
      }
      else if ("Description" in obj) {
        FieldName = obj.Description
      }

      let savedDefault = defaults.find(x => x.FieldName === FieldName)
      let newEl = this.createOrderDefaultElement(FieldName, lookup, savedDefault)
      result.push(newEl)
    })

    return result
  }

  handleCancel = () => (this.props as RouteComponentProps).history?.push(Home)

  handleSave = () => {
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.setState({ saving: true })

    var saveObj: SaveOrderDefaults = {
      Defaults: this.state.data
    }
    this.saveSubscription = OrderDefaultsSave(saveObj).subscribe(
      (data) => {
        this.setState({saving: false, showWarning: false})
        this.loadDefaults(this.state.selectedLookup)
        toast.success(data)
      },
      (error) => this.handleAPIError(error, { saving: false })
    )
  }

  handleReset = () => {
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.setState({ resetting: true })
    this.saveSubscription = OrderDefaultsReset({ImplementationID: this.state.selectedPortal?.ImplementationID || 0, AccountID: this.state.selectedCompany?.AccountID, LookupCode: this.state.selectedLookup?.Code || ""}).subscribe(
      (data) => {
        this.setState({resetting: false, selectedLookup: undefined, data: [], defaults: []})
        toast.success(data)
      },
      (error) => this.handleAPIError(error, { resetting: false })
    )
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error(FormatError(error))
  }

  toggleShow = (index: number) => {
    const newData: OrderDefault[] = [...this.state.data]
    newData[index].Show = !newData[index].Show
    newData[index].IsDefault = false
    this.setState({ data: newData })
  }

  toggleDefault = (index: number) => {
    const newData: OrderDefault[] = [...this.state.data]
    const defaultValue = !newData[index].IsDefault
    newData.forEach(el => el.IsDefault = false)
    newData[index].IsDefault = defaultValue
    this.setState({ data: newData })
  }

  handleCompanyChange = (selected: AutoFilterCompany[]) => {
    const selectedCompany = selected[0]
    this.setState({ selectedCompany: selectedCompany })
    if (selectedCompany) {
      this.setState({ selectedLookup: undefined, defaults: [], data: [], showWarning: false })
    }
  }

  handleEmpty = (company: string) => {
    if (company == "") {
       this.setState({ defaults: [], data: [], selectedPortal: undefined, selectedLookup: undefined, showWarning: false })
    }
  }

  handlePortalChange = (selected: ImplementationData[]) => {
    const selectedPortal = selected[0]
    this.setState({ selectedPortal: selectedPortal })
    if (selectedPortal) {
      this.setState({ selectedLookup: undefined, defaults: [], data: [], showWarning: false })
    }
  }

  handleLookupChange = (selected: string) => {
    const lookup = LookupMap.find(el => el.Code === selected)
    this.setState({ selectedLookup: lookup })
    if (!!lookup) {
      this.loadDefaults(lookup)
    }
    else {
      this.setState({ defaults: [], data: [], showWarning: false })
    }
  }

  handleDefaultValueChange = (value: string, index: number) => {
    const newData: OrderDefault[] = [...this.state.data]
    newData[index].DefaultValue = value
    this.setState({ data: newData })
  }

  isAnyShowSelected = () => this.state.data.some(el => !!el.Show)

  DataRow = (data: OrderDefault, index: number, lookup?: Lookup) => {
    return <tr key={`defaultRow${index}`}>
      <td className="align-middle">{data.FieldName}</td>
      {!lookup?.IsInput && <>
        <td><Checkbox id={`toggleShow${index}`} checked={data.Show} disabled={data.Disable} onChange={() => this.toggleShow(index)} className="mr-auto" /></td>
        <td><Checkbox id={`toggleDefault${index}`} checked={data.IsDefault} disabled={!data.Show} onChange={() => this.toggleDefault(index)} className="mr-auto" /></td>
      </>}
      {lookup?.IsInput && !lookup?.IsNumeric && <td><Form.Control id={`input${index}`} className="bg-white" type="text" value={data.DefaultValue || ""} onChange={e => this.handleDefaultValueChange(e.target.value, index)}/></td>}
      {lookup?.IsInput && lookup?.IsNumeric && <td><Form.Control as={NumericInput} id={`input${index}`} min={1} max={999} value={data.DefaultValue} strict onChange={(value: any) => this.handleDefaultValueChange(value, index)} /></td>}
    </tr>
  }

  render() {
    const { saving, loading, resetting, loadingDefaults, data, selectedCompany, selectedPortal, selectedLookup, portals, showWarning } = this.state
    const disableButtons = (!selectedCompany && !selectedPortal) || !selectedLookup || loading || saving || resetting || loadingDefaults
    const { isSiteAdmin, isPortalAdmin } = this.props
    const showSaveWarning = data.length > 0 && !this.isAnyShowSelected() && !this.state.selectedLookup?.IsInput

    return (
      <AppPageWrapper showNav>
        <Modal.Dialog size="lg">
          <Modal.Header>
            <Modal.Title className="text-uppercase">Order Defaults</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {isPortalAdmin && <Form.Group as={Row}>
              <Form.Label column lg={2}>Company:</Form.Label>
              <Col>
                <CompanyInput filter disabled={loading || loadingDefaults} onChange={this.handleCompanyChange} />
              </Col>
            </Form.Group>}
            {isSiteAdmin && <Form.Group as={Row}>
              <Form.Label column lg={2}>Portal:</Form.Label>
                <Col>
                  <Typeahead id="portal"
                    ref={this.portalTypeahead}
                    labelKey="Name"
                    filterBy={(option, props) => props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                    isLoading={loading}
                    onChange={this.handlePortalChange}
                    onInputChange={text => this.handleEmpty(text)}
                    disabled={loading || loadingDefaults}
                    options={portals || []}
                    selected={selectedPortal ? [selectedPortal || ""] : []}
                    placeholder="Select Portal"
                    emptyLabel="No Portals Found">
                    {!loading && <Button variant="toggle" onClick={() => { this.portalTypeahead.current?.toggleMenu() }}>
                      <FaAngleDown />
                    </Button>}
                  </Typeahead>
                </Col>
              </Form.Group>}
            <Form.Group as={Row}>
              <Form.Label column lg={2}>Order Field:</Form.Label>
              <Col>
                <Form.Control id="lookupCode"
                  as="select"
                  disabled={(!selectedCompany && !selectedPortal) || loading || loadingDefaults}
                  value={selectedLookup?.Code || ""}
                  onChange={e => this.handleLookupChange(e.target.value)}>
                  <option value="">Select</option>
                  {LookupMap.map(el => <option value={el.Code} key={el.Code}>{el.Label}</option>)}
                </Form.Control>
              </Col>
            </Form.Group>
            {showWarning && <Form.Group><Col className="note-message font-italic px-3 text-center">No defaults configured for selected Order Field</Col></Form.Group>}
            {showSaveWarning && <Form.Group><Col className="note-message font-italic px-3 text-center">At least one option should be selected to Show before update</Col></Form.Group>}
            {data.length > 0 && <Form.Group as={Row}>
              <Table responsive bordered className="dark text-center">
                <thead>
                  <tr>
                    {!selectedLookup?.IsInput && <><th className="w-50">Field Value</th><th className="w-25">Show</th><th className="w-25">Make Default</th></>}
                    {selectedLookup?.IsInput && <><th className="w-50">Field</th><th className="w-50">Default Value</th></>}
                  </tr>
                </thead>
                <tbody>{this.state.loadingDefaults ? <tr><td colSpan={2}><Spinner animation="border" size="sm" /></td></tr> :
                  data.map((value, index) => this.DataRow(value, index, this.state.selectedLookup))}</tbody>
              </Table>
            </Form.Group>}
          </Modal.Body>
          <Modal.Footer>
            <ActionButton variant="primary" onClick={() => this.handleReset()} disabled={disableButtons || showWarning} spin={resetting}>Reset Defaults</ActionButton>
            <ActionButton variant="primary" onClick={() => this.handleSave()} disabled={disableButtons || showSaveWarning} spin={saving}>Update</ActionButton>
            <Button variant="secondary" className="mr-2" onClick={() => this.handleCancel()}>Cancel</Button>
          </Modal.Footer>
        </Modal.Dialog>
      </AppPageWrapper>
    )
  }
}

function mapStateToProps(state: AppState): OrderDefaultsAuthProps {
  return {
    isPortalAdmin: isPortalAdmin(state),
    isSiteAdmin: isSiteAdmin(state)
  }
}
const ConnectedOrderDefaults = connect(mapStateToProps, null)(OrderDefaults)
export default ConnectedOrderDefaults